import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Input,
  SpaceBetween,
  StatusIndicator,
  Tabs,
} from "@amzn/awsui-components-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { fetchArqaData, fetchPresignedUrl } from "./ArqaAPIs";
import ArqaResultReport from "./ArqaResultReport";
import ArqaRatingsPieChart from "./ArqaRatingsPieChart";
import aiInsights from '../../images/aiInsights.svg';
import { Auth } from "aws-amplify";

const ArqaHome = () => {
  const gridRef = useRef();
  const history = useHistory();
  const location = useLocation();
  const userId = useSelector((globalState) => globalState.auth.userId);
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [error, setError] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [resultTabs, setResultTabs] = useState([]);
  const [viewingResults, setViewingResults] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [ratings, setRatings] = useState(null);
  const [teamCounts, setTeamCounts] = useState(null);

  const defaultColDef = useMemo(() => ({
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      debounceMs: 200,
    },
    resizable: true,
    sortable: true,
  }), []);

  const RATING_COLORS = {
    Green: '#1d8102',
    Yellow: '#d4a017',
    Red: '#d13212',
    Error: '#5f6b7a',
  };

  const ratingCellRenderer = (params) => {
    if (!params.value) return '';
    const color = RATING_COLORS[params.value] || '#5f6b7a';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: color,
          display: 'inline-block',
        }} />
        {params.value}
      </span>
    );
  };

  const buildColumnDefs = (firstItem) => {
    if (!firstItem) return [];
    return Object.keys(firstItem)
      .filter((key) => {
        const val = firstItem[key];
        return !(typeof val === 'string' && val.startsWith('s3://'));
      })
      .map((key, index) => {
        const col = {
          field: key,
          headerName: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          flex: 1,
          minWidth: 120,
        };
        if (index === 0) {
          col.checkboxSelection = true;
          col.headerCheckboxSelection = true;
        }
        if (key === 'overall_rating') {
          col.cellRenderer = ratingCellRenderer;
        } else if (typeof firstItem[key] === 'string' && (firstItem[key].startsWith('http://') || firstItem[key].startsWith('https://'))) {
          col.cellRenderer = (params) => {
            if (!params.value) return '';
            return <a href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</a>;
          };
        }
        return col;
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-load results from URL query params
  const urlLoadedRef = useRef(false);
  useEffect(() => {
    if (urlLoadedRef.current) return;
    const params = new URLSearchParams(location.search);
    const recIds = params.get('rec_ids');
    if (!recIds || rowData.length === 0) return;

    urlLoadedRef.current = true;
    const ids = recIds.split(',');
    const matchingRows = rowData.filter(r => ids.includes(r.rec_id));
    if (matchingRows.length === 0) return;

    setSelectedRows(matchingRows);

    // Select matching rows in the grid
    setTimeout(() => {
      if (gridRef?.current?.api) {
        gridRef.current.api.forEachNode(node => {
          if (node.data && ids.includes(node.data.rec_id)) {
            node.setSelected(true);
          }
        });
      }
    }, 100);

    // Load results
    (async () => {
      setViewingResults(true);
      const tabs = [];
      try {
        const session = await Auth.currentSession();
        const jwtToken = session.getIdToken().getJwtToken();
        for (const row of matchingRows) {
          const s3Key = getDetailPath(row);
          if (!s3Key) continue;
          const tabLabel = `${row.item_name || row.rec_id || 'Result'} (${row.team || ''})`.trim();
          try {
            const presignedUrl = await fetchPresignedUrl(jwtToken, s3Key);
            const response = await fetch(presignedUrl);
            const jsonData = await response.json();
            tabs.push({ label: tabLabel, id: row.rec_id || s3Key, content: (<Box padding="l"><ArqaResultReport data={jsonData} /></Box>) });
          } catch (err) {
            tabs.push({ label: tabLabel, id: row.rec_id || String(Math.random()), content: (<StatusIndicator type="error">Failed to load: {err.message}</StatusIndicator>) });
          }
        }
        setResultTabs(tabs);
      } catch (err) {
        setError(err.message);
      } finally {
        setViewingResults(false);
      }
    })();
  }, [rowData]);

  useEffect(() => {
    gridRef?.current?.api?.setGridOption("quickFilterText", quickFilterText);
  }, [quickFilterText]);

  const onSelectionChanged = () => {
    const selected = gridRef?.current?.api?.getSelectedRows() || [];
    setSelectedRows(selected);
  };

  const computeRatingsFromVisibleRows = () => {
    if (!gridRef?.current?.api) return;
    const ratingCounts = {};
    const teamCountsRaw = {};
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      const rating = node.data?.overall_rating || 'Not Analyzed';
      ratingCounts[rating] = (ratingCounts[rating] || 0) + 1;
      const team = node.data?.team || 'Unknown';
      teamCountsRaw[team] = (teamCountsRaw[team] || 0) + 1;
    });
    setRatings(ratingCounts);
    // Limit teams to top 10, group rest as "Others"
    const sorted = Object.entries(teamCountsRaw).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 10);
    const othersCount = sorted.slice(10).reduce((sum, [, v]) => sum + v, 0);
    const limited = Object.fromEntries(top);
    if (othersCount > 0) limited['Others'] = othersCount;
    setTeamCounts(limited);
  };

  const onFilterChanged = () => {
    computeRatingsFromVisibleRows();
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await Auth.currentSession();
      const jwtToken = session.getIdToken().getJwtToken();
      const { items } = await fetchArqaData(jwtToken);
      if (items.length > 0) {
        setColumnDefs(buildColumnDefs(items[0]));
      }
      setRowData(items);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const getDetailPath = (row) => {
    // Find the first s3:// value in the row — that's the detail path
    for (const val of Object.values(row)) {
      if (typeof val === 'string' && val.startsWith('s3://')) {
        // Extract the key from s3://bucket/key
        const withoutProtocol = val.replace('s3://', '');
        const slashIndex = withoutProtocol.indexOf('/');
        return withoutProtocol.substring(slashIndex + 1);
      }
    }
    return null;
  };

  const viewResults = async () => {
    setViewingResults(true);
    setError(null);
    const tabs = [];

    try {
      const session = await Auth.currentSession();
      const jwtToken = session.getIdToken().getJwtToken();

      for (const row of selectedRows) {
        const s3Key = getDetailPath(row);
        if (!s3Key) continue;

        const tabLabel = `${row.item_name || row.rec_id || 'Result'} (${row.team || ''})`.trim();

        try {
          const presignedUrl = await fetchPresignedUrl(jwtToken, s3Key);
          const response = await fetch(presignedUrl);
          const jsonData = await response.json();

          tabs.push({
            label: tabLabel,
            id: row.rec_id || s3Key,
            content: (
              <Box padding="l">
                <ArqaResultReport data={jsonData} />
              </Box>
            ),
          });
        } catch (err) {
          tabs.push({
            label: tabLabel,
            id: row.rec_id || String(Math.random()),
            content: (
              <StatusIndicator type="error">
                Failed to load: {err.message}
              </StatusIndicator>
            ),
          });
        }
      }

      setResultTabs(tabs);
      // Update URL with rec_ids for shareable link
      const recIds = selectedRows.map(r => r.rec_id).filter(Boolean).join(',');
      if (recIds) {
        history.replace(`${location.pathname}?rec_ids=${encodeURIComponent(recIds)}`);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch results");
    } finally {
      setViewingResults(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {showSidePanel && (
        <div style={{ width: '300px', minWidth: '300px', padding: '16px', borderRight: '1px solid #eaeded', overflowY: 'auto' }}>
          <SpaceBetween direction="vertical" size="s">
            <Box variant="h2">ARQA</Box>
            <Box variant="h3">Hello, {userId}!</Box>
            <Box>
              <strong>ARQA (Account Reconciliation Quality Assurance)</strong> is
              an AI agent that automates end-to-end balance sheet reconciliation
              reviews — reading files, evaluating quality, assigning ratings,
              updating repositories, and drafting feedback emails.
            </Box>

            <Box variant="h4">Rating Framework</Box>
            <Box>
              <ul>
                <li><span style={{ color: '#1d8102', fontWeight: 'bold' }}>Green</span> — Meets standards, no action required</li>
                <li><span style={{ color: '#d4a017', fontWeight: 'bold' }}>Yellow</span> — Correctable gaps, fix within a month</li>
                <li><span style={{ color: '#d13212', fontWeight: 'bold' }}>Red</span> — Structural deficiencies, immediate escalation</li>
              </ul>
            </Box>
            <Box>
              <ol>
                <li>Select one or more reconciliations</li>
                <li>Click "View Results" to see detail</li>
              </ol>
            </Box>
            <Box>
              <a href="https://w.amazon.com/bin/view/ARQA/" target="_blank" rel="noopener noreferrer">
                More info on the ARQA Wiki →
              </a>
            </Box>
          </SpaceBetween>
        </div>
      )}
      <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
        <SpaceBetween direction="vertical" size="l">
          {ratings && (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {(() => { const total = Object.values(ratings).reduce((s, v) => s + v, 0); const pct = (v) => total > 0 ? ((v / total) * 100).toFixed(2) : '0.00'; return (<>
              <div style={{ flex: 1, minWidth: '120px', padding: '12px 16px', border: '1px solid #eaeded', borderRadius: '8px', textAlign: 'center' }}>
                <Box variant="awsui-key-label">📊 Total</Box>
                <Box variant="h2">{total}</Box>
              </div>
              <div style={{ flex: 1, minWidth: '120px', padding: '12px 16px', border: '1px solid #1d8102', borderRadius: '8px', textAlign: 'center', backgroundColor: '#f2fcf3' }}>
                <Box variant="awsui-key-label" color="text-status-success">✅ Green</Box>
                <Box variant="h2" color="text-status-success">{ratings['Green'] || 0} <span style={{fontSize: '14px', fontWeight: 'normal'}}>({pct(ratings['Green'] || 0)}%)</span></Box>
              </div>
              <div style={{ flex: 1, minWidth: '120px', padding: '12px 16px', border: '1px solid #d4a017', borderRadius: '8px', textAlign: 'center', backgroundColor: '#fef8e7' }}>
                <Box variant="awsui-key-label" color="text-status-warning">⚠️ Yellow</Box>
                <Box variant="h2" color="text-status-warning">{ratings['Yellow'] || 0} <span style={{fontSize: '14px', fontWeight: 'normal'}}>({pct(ratings['Yellow'] || 0)}%)</span></Box>
              </div>
              <div style={{ flex: 1, minWidth: '120px', padding: '12px 16px', border: '1px solid #d13212', borderRadius: '8px', textAlign: 'center', backgroundColor: '#fdf3f1' }}>
                <Box variant="awsui-key-label" color="text-status-error">❌ Red</Box>
                <Box variant="h2" color="text-status-error">{ratings['Red'] || 0} <span style={{fontSize: '14px', fontWeight: 'normal'}}>({pct(ratings['Red'] || 0)}%)</span></Box>
              </div>
              <div style={{ flex: 1, minWidth: '120px', padding: '12px 16px', border: '1px solid #aab7b8', borderRadius: '8px', textAlign: 'center', backgroundColor: '#f9fafa' }}>
                <Box variant="awsui-key-label" color="text-status-inactive">⏳ Not Analyzed</Box>
                <Box variant="h2" color="text-status-inactive">{ratings['Not Analyzed'] || 0} <span style={{fontSize: '14px', fontWeight: 'normal'}}>({pct(ratings['Not Analyzed'] || 0)}%)</span></Box>
              </div>
              </>); })()}
            </div>
          )}
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 1 }}>
              <SpaceBetween direction="vertical" size="s">
                <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
                  <SpaceBetween direction="horizontal" size="s">
                    <Button
                      iconName={showSidePanel ? "angle-left" : "angle-right"}
                      variant="icon"
                      onClick={() => setShowSidePanel(!showSidePanel)}
                    />
                    <Input
                      onChange={({ detail }) => setQuickFilterText(detail.value)}
                      value={quickFilterText}
                      placeholder="Type to search"
                    />
                  </SpaceBetween>
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="s">
                      <Button
                        onClick={viewResults}
                        disabled={selectedRows.length === 0 || viewingResults}
                        variant="primary"
                        loading={viewingResults}
                      >
                        View Results
                      </Button>
                      <Button onClick={loadData} iconName="refresh">
                        Refresh
                      </Button>
                      <Button
                        iconName={showRightPanel ? "angle-right" : "angle-left"}
                        variant="icon"
                        onClick={() => setShowRightPanel(!showRightPanel)}
                      />
                    </SpaceBetween>
                  </Box>
                </Grid>

                {error && (
                  <StatusIndicator type="error">{error}</StatusIndicator>
                )}

                <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    onSelectionChanged={onSelectionChanged}
                    onFilterChanged={onFilterChanged}
                    onFirstDataRendered={computeRatingsFromVisibleRows}
                    onRowDataUpdated={computeRatingsFromVisibleRows}
                    isRowSelectable={(node) => {
                      const data = node.data;
                      if (!data) return false;
                      return Object.values(data).some(
                        (val) => typeof val === 'string' && val.startsWith('s3://')
                      );
                    }}
                    animateRows={true}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={[10, 50, 100]}
                    loading={isLoading}
                  />
                </div>
              </SpaceBetween>
            </div>
            {ratings && showRightPanel && (
              <div style={{ width: '400px', minWidth: '400px', padding: '0 16px', borderLeft: '1px solid #eaeded', overflowY: 'auto', maxHeight: '650px' }}>
                <SpaceBetween direction="vertical" size="m">
                  <ArqaRatingsPieChart ratings={ratings} title="By Status" />
                  {teamCounts && <ArqaRatingsPieChart ratings={teamCounts} title="By Team" />}
                </SpaceBetween>
              </div>
            )}
          </div>

          {resultTabs.length > 0 && (
            <SpaceBetween direction="vertical" size="m">
              <hr />
              <SpaceBetween direction="vertical" size="xxs">
                <Box variant="h3"><img className="ai-button" src={aiInsights} alt="" /> Results Generated by AI</Box>
                <Box><i>AI has limitations and may produce omissions or errors. Please review all important information. Year: {rowData?.[0]?.year || ''}, Period: {rowData?.[0]?.period || ''}</i></Box>
              </SpaceBetween>
              <Tabs tabs={resultTabs} />
            </SpaceBetween>
          )}
        </SpaceBetween>
      </div>
    </div>
  );
};

export default ArqaHome;
